import { z } from 'zod'

import { MESSAGE_TYPES, WhatsAppMessageFailedErrorCodes } from 'apps/chat/constants/chatConstants'
import { LiveChatNoteTypes } from 'apps/chat/constants/LiveChatNoteTypes'
import { CommonChannelFormType } from 'apps/chat/controllers/form/channel/Common/types'
import { ChatMessageBlockType } from 'apps/chat/types/chatMessagesBlockList'
import { ChatMessageType } from 'apps/chat/types/chatMessageTypeList'
import { MessageTemplateAttachment } from 'apps/whatsApp/core/constants'
import { ChannelType } from 'common/core/constants/ChannelType'

import { ReducedUserSchema } from '../audience/schemas'

const nullishNumber = z.number().nullable()
const emptyArray = z.array(z.any()).length(0)

const sendRequestBody = z.object({
  pause_automation: z.boolean().optional(),
  text: z.string().optional(),
  timestamp: z.number(),
  type: z.nativeEnum(ChatMessageBlockType),
  user_id: z.string(),
})

const msgOutText = z.object({
  type: z.literal(ChatMessageBlockType.TEXT),
  _oid: z.string().optional(),
  keyboard: z.array(z.unknown()).nullable().optional(),
  content: z.object({
    text: z.string(),
  }),
})

const msgOutSMS = z.object({
  type: z.literal(ChatMessageBlockType.SMS),
  _oid: z.string().optional(),
  content: z.object({
    text: z.string(),
  }),
})

const msgOutEmail = z.object({
  description: z.string(),
})

const msgOutWhatsAppTemplate = z.object({
  type: z.literal(ChatMessageBlockType.TEMPLATE),
  _oid: z.string(),
  _mid: z.string(),
  error_code: z.nativeEnum(WhatsAppMessageFailedErrorCodes).optional(),
  keyboard: z.array(z.unknown()).nullable().optional(),
  content: z.object({
    name: z.string(),
    name_human: z.string(),
    body: z.string().optional(),
    footer: z.string().optional(),
    header: z.union([
      z.object({
        type: z.nativeEnum(MessageTemplateAttachment),
        url: z.string().optional(),
        name: z.string().optional(),
        text: z.string().optional(),
      }),
      emptyArray,
    ]),
  }),
})

const msgOutFileAttachment = z.object({
  type: z.literal(ChatMessageBlockType.ATTACHMENT),
  content: z.object({
    uploading: z.boolean().optional(),
    type: z
      .union([z.literal(ChatMessageBlockType.FILE), z.literal(ChatMessageBlockType.AUDIO)])
      .optional(),
    data: z
      .object({
        type: z.literal(ChatMessageBlockType.FILE),
        caid: z.number(),
        file: z.string(),
        mime: z.string(),
        title: z.string(),
        sign: z.string(),
      })
      .optional(),
  }),
})

const msgOutVoiceMessage = z.object({
  type: z.literal(ChatMessageBlockType.ATTACHMENT),
  content: z.object({
    uploading: z.boolean().optional(),
    type: z.literal(ChatMessageBlockType.VOICE),
    data: z.object({
      is_voice: z.boolean(),
      attachment_id: z.number().optional(),
    }),
  }),
})

const msgOutImageAttachment = z.object({
  type: z.literal(ChatMessageBlockType.ATTACHMENT),
  content: z.object({
    uploading: z.boolean().optional(),
    type: z.literal(ChatMessageBlockType.IMAGE),
    data: z.object({
      type: z.literal(ChatMessageBlockType.IMAGE),
      caid: z.number(),
      img_big: z.string(),
      img_small: z.string(),
      title: z.string(),
      image_size: z
        .object({
          width: z.number(),
          height: z.number(),
          size: z.number(),
        })
        .optional(),
      img_original: z.string().nullable(),
      sign: z.string(),
    }),
  }),
})

const personaData = z
  .object({
    persona_api_avatar_url: z.string().optional(),
    persona_api_name: z.string().optional(),
    persona_id: z.string().optional().nullable(),
  })
  .nullable()

const incomingMessageType = z.union([
  z.literal(ChatMessageType.MSGIN_INSTAGRAM),
  z.literal(ChatMessageType.MSGIN_WHATSAPP),
  z.literal(ChatMessageType.MSGIN_TELEGRAM),
  z.literal(ChatMessageType.MSGIN_SMS),
  z.literal(ChatMessageType.MSGIN_TIKTOK),
  z.literal(ChatMessageType.MSGIN),
  z.literal(ChatMessageType.MSGIN_LC_TELEGRAM),
  z.literal(ChatMessageType.CLICK_URL),
  z.literal(ChatMessageType.CLICK_ACTION),
])

const incomingMessageSchema = z.object({
  message_id: z.string(),
  model: z.object({
    type: z.nativeEnum(ChatMessageBlockType),
  }),
  type: incomingMessageType,
  user_id: z.string(),
})

const generateOutgoingMessageSchema = (channel?: ChannelType) => {
  const typicalOutModel = z.object({
    messages: generateMessagesUnion(channel),

    sender: z.union([
      personaData,
      z
        .object({
          avatar: z.string(),
          profile_id: z.number(),
          user_id: z.number(),
          user_name: z.string(),
        })
        .optional(),
    ]),
    _oid: z.string().optional(),
  })

  if (channel === ChannelType.INSTAGRAM) {
    return z.object({
      message_id: z.string(),
      sending: z.boolean().optional(),
      sendError: z.boolean().optional(),
      attachment_id: z.array(z.number()).optional(),
      model: z.union([typicalOutModel, emptyArray, z.object({ is_deleted: z.boolean() })]),
      type: z.nativeEnum(ChatMessageType),
      user_id: z.string(),
    })
  }
  return z.object({
    message_id: z.string(),
    sending: z.boolean().optional(),
    sendError: z.boolean().optional(),
    attachment_id: z.array(z.number()).optional(),
    // model is optional because WA template messages don't have model for outgoing messages
    model: z.union([typicalOutModel, emptyArray]).optional(),
    type: z.nativeEnum(ChatMessageType),
    user_id: z.string(),
  })
}

const systemMessageSchema = z.object({
  message_id: z.string(),
  type: z.nativeEnum(ChatMessageType),
  model: z.record(z.unknown()),
  user_id: z.string(),
})

const createMessageSchema = (channel?: ChannelType) =>
  z.union([generateOutgoingMessageSchema(channel), incomingMessageSchema])

const generateMessagesUnion = (channel?: ChannelType) => {
  let messages
  switch (channel) {
    case ChannelType.INSTAGRAM:
      messages = z.array(
        z.union([msgOutText, msgOutImageAttachment, msgOutFileAttachment, msgOutVoiceMessage]),
      )
      break
    case ChannelType.WHATSAPP:
      messages = z.array(
        z.union([
          msgOutWhatsAppTemplate,
          msgOutText,
          msgOutFileAttachment,
          msgOutImageAttachment,
          msgOutVoiceMessage,
        ]),
      )
      break
    case ChannelType.SMS:
      messages = z.array(msgOutSMS)
      break
    case ChannelType.TELEGRAM:
      messages = z.array(
        z.union([msgOutText, msgOutFileAttachment, msgOutImageAttachment, msgOutVoiceMessage]),
      )
      break
    case ChannelType.TIKTOK:
      messages = z.array(z.union([msgOutText, msgOutImageAttachment]))
      break
    case ChannelType.FB:
      messages = z.array(
        z.union([msgOutText, msgOutFileAttachment, msgOutImageAttachment, msgOutVoiceMessage]),
      )
      break
    case ChannelType.EMAIL:
      messages = z.array(msgOutEmail)
      break
    default:
      messages = z.array(z.union([msgOutText, msgOutFileAttachment, msgOutImageAttachment]))
      break
  }
  return messages
}

const chatMessageFacebook = createMessageSchema()
const chatMessageWhatsApp = createMessageSchema(ChannelType.WHATSAPP)
const chatMessageSMS = createMessageSchema(ChannelType.SMS)
const chatMessageTelegram = createMessageSchema(ChannelType.TELEGRAM)
const chatMessageTiktok = createMessageSchema(ChannelType.TIKTOK)
const chatMessageInstagram = createMessageSchema(ChannelType.INSTAGRAM)
const chatMessageEmail = createMessageSchema(ChannelType.EMAIL)

const createSendMessageSchema = (channel: CommonChannelFormType) => {
  const messages = generateMessagesUnion(channel)

  return {
    query: z
      .object({
        clear_question_context: z.string(),
      })
      .optional(),
    path: z.undefined(),
    request: sendRequestBody,
    response: z.object({
      messages: z.array(
        z.object({
          model: z.object({
            messages,
            sender: z.object({
              user_id: z.number(),
              user_name: z.string(),
              profile_id: z.number(),
              avatar: z.string().nullable(),
              persona_id: z.number().nullable().optional(),
            }),
          }),
          type: z.literal(MESSAGE_TYPES[channel]),
          _oid: z.string().optional(),
        }),
      ),
    }),
  }
}

export const ChatApiSchemas = {
  markThreadAsUnread: {
    query: z.undefined(),
    path: z.undefined(),
    request: z.object({
      user_id: z.string(),
    }),
    response: z.undefined(),
  },
  getUserLocation: {
    query: z.undefined(),
    path: z.undefined(),
    request: z.undefined(),
    response: z.object({
      coordinates: z.object({
        longitude: z.string(),
        latitude: z.string(),
      }),
    }),
  },
  setAutomationPauseForever: {
    query: z.undefined(),
    path: z.undefined(),
    request: z.object({
      user_id: z.string(),
    }),
    response: z.object({
      thread: ReducedUserSchema.optional(),
    }),
  },
  setAutomationPause: {
    query: z.undefined(),
    path: z.undefined(),
    request: z.object({
      user_id: z.string(),
      delay: z.number(),
    }),
    response: z.object({
      thread: ReducedUserSchema.optional(),
    }),
  },
  clearPause: {
    query: z.undefined(),
    path: z.undefined(),
    request: z.object({
      user_id: z.string(),
    }),
    response: z.object({
      thread: ReducedUserSchema.optional(),
    }),
  },
  messages: {
    fetch: {
      query: z.object({
        limiter: z.number().optional(),
        user_id: z.string().optional(),
        type: z.nativeEnum(ChannelType).optional(),
        limiter_type: z.string().optional(),
        hide_automation: z.union([z.literal('0'), z.literal('1')]).optional(),
      }),
      path: z.undefined(),
      request: z.undefined(),
      response: z.object({
        later_limiter: nullishNumber,
        limiter: nullishNumber,
        messages: z.array(
          z.union([
            chatMessageFacebook,
            chatMessageWhatsApp,
            chatMessageSMS,
            chatMessageTelegram,
            chatMessageTiktok,
            chatMessageInstagram,
            chatMessageEmail,
            systemMessageSchema,
          ]),
        ),
      }),
    },
    sendFacebook: createSendMessageSchema(ChannelType.FB),
    sendInstagram: createSendMessageSchema(ChannelType.INSTAGRAM),
    sendWhatsApp: createSendMessageSchema(ChannelType.WHATSAPP),
    sendTelegram: createSendMessageSchema(ChannelType.TELEGRAM),
    sendSMS: createSendMessageSchema(ChannelType.SMS),
    sendTiktok: createSendMessageSchema(ChannelType.TIKTOK),
    sendFlow: {
      query: z.undefined(),
      path: z.undefined(),
      request: z.object({
        user_id: z.string(),
        flow_ns: z.string(),
        payload: z.object({
          type: z.literal('live_chat'),
        }),
      }),
      response: z.undefined(),
    },
    createNote: {
      query: z.undefined(),
      path: z.undefined(),
      request: z.object({
        user_id: z.string(),
        text: z.string().optional(),
        type: z.nativeEnum(ChatMessageBlockType),
        attachment_id: z.array(z.number()).optional(),
        channel: z.nativeEnum(ChannelType),
      }),
      response: z.object({
        messages: z.array(
          z.object({
            model: z.object({
              messages: z.array(
                z.object({
                  content: z.unknown(),
                  type: z.nativeEnum(ChatMessageBlockType),
                }),
              ),
              sender: personaData,
            }),
            page_id: z.string(),
            type: z.nativeEnum(LiveChatNoteTypes),
            user_id: z.string(),
            _oid: z.string(),
          }),
        ),
      }),
    },
  },
}

export type SendMessageResponse = z.infer<
  | typeof ChatApiSchemas.messages.sendFacebook.response
  | typeof ChatApiSchemas.messages.sendInstagram.response
  | typeof ChatApiSchemas.messages.sendWhatsApp.response
  | typeof ChatApiSchemas.messages.sendTelegram.response
  | typeof ChatApiSchemas.messages.sendSMS.response
  | typeof ChatApiSchemas.messages.sendTiktok.response
>
export type CreateNoteResponse = z.infer<typeof ChatApiSchemas.messages.createNote.response>
type APIMessage = z.infer<typeof chatMessageFacebook>
type APIMessageInstagram = z.infer<typeof chatMessageInstagram>
type APIMessageWhatsApp = z.infer<typeof chatMessageWhatsApp>
type APIMessageSMS = z.infer<typeof chatMessageSMS>
type APIMessageTelegram = z.infer<typeof chatMessageTelegram>
type APIMessageTiktok = z.infer<typeof chatMessageTiktok>
type APIMessageEmail = z.infer<typeof chatMessageEmail>
export type Message =
  | APIMessage
  | APIMessageInstagram
  | APIMessageWhatsApp
  | APIMessageSMS
  | APIMessageTelegram
  | APIMessageTiktok
  | APIMessageEmail
export type PersonaData = z.infer<typeof personaData>
export type MessageForSend = Message & { attachment_id?: number[] }

export type IncomingMessage = z.infer<typeof incomingMessageSchema>
export type OutgoingMessage = z.infer<ReturnType<typeof generateOutgoingMessageSchema>>
